import { celsiusToFahrenheitConvertor } from 'src/utils/utils';

export type TemperatureUnit = 'C' | 'F';

/** Indexed by the API's `dayIndex` (moment's `.day()`, 0 = Sunday). */
export const DAY_KEYS = [
  'weather.day.sunday',
  'weather.day.monday',
  'weather.day.tuesday',
  'weather.day.wednesday',
  'weather.day.thursday',
  'weather.day.friday',
  'weather.day.saturday',
];

export interface AmbientTemperatureDay {
  key: string;
  label: string;
  high: string;
  low: string;
  isHighFreezing: boolean;
  isLowFreezing: boolean;
}

/** The design shows five days; the API usually returns six. */
export const FORECAST_DAY_COUNT = 5;

const DEGREE = String.fromCharCode(176);

/* The API sends temperatures pre-formatted ("10.0°C"), so anything numeric --
 * the freeze test, the badge count, °F -- has to parse them back out. */
export function parseTemperature (value?: string | null): number | null {
  if (typeof value !== 'string') return null;

  const parsed = parseFloat(value.replace(/[^\d.-]/g, ''));

  return Number.isFinite(parsed) ? parsed : null;
}

export function formatTemperature (
  celsius: number | null,
  unit: TemperatureUnit,
  { withUnit = false }: { withUnit?: boolean } = {}
): string {
  if (celsius === null) return '--';

  const value = unit === 'F' ? celsiusToFahrenheitConvertor(celsius) : celsius;

  return withUnit ? `${value}${DEGREE}${unit}` : `${value}${DEGREE}`;
}

export function isFreezing (celsius: number | null): boolean {
  return celsius !== null && celsius <= 0;
}

/** The threshold as the badge spells it: 0°C or 32°F. */
export function freezingLabel (unit: TemperatureUnit): string {
  return formatTemperature(0, unit, { withUnit: true });
}

function dayLabel (day: any, t: (key: string) => string): string {
  if (day.get('isToday')) return t('weather.day.today');
  if (day.get('isTomorrow')) return t('weather.day.tomorrow');

  const dayIndex = day.get('dayIndex');

  return dayIndex !== undefined && DAY_KEYS[dayIndex] ? t(DAY_KEYS[dayIndex]) : day.get('day') || '';
}

export function toForecastDays (
  forecast: any,
  { unit, t }: { unit: TemperatureUnit; t: (key: string) => string }
): AmbientTemperatureDay[] {
  if (!forecast || typeof forecast.reduce !== 'function') return [];

  return forecast.reduce((days: AmbientTemperatureDay[], day: any, key: string) => {
    if (!day || days.length >= FORECAST_DAY_COUNT) return days;

    const highCelsius = parseTemperature(day.get('high'));
    const lowCelsius = parseTemperature(day.get('low'));
    const highFreezing = isFreezing(highCelsius);
    const lowFreezing = isFreezing(lowCelsius);

    days.push({
      key,
      label: dayLabel(day, t),
      high: formatTemperature(highCelsius, unit, { withUnit: highFreezing }),
      low: formatTemperature(lowCelsius, unit, { withUnit: lowFreezing }),
      isHighFreezing: highFreezing,
      isLowFreezing: lowFreezing,
    });

    return days;
  }, []);
}

export function countFreezingDays (days: AmbientTemperatureDay[]): number {
  return days.filter((day) => day.isLowFreezing).length;
}
